import { inject, injectable } from 'inversify'

import { InjectionTokens } from '@/controller/tokens'

import { UniqueEntityID } from '@/domain/core'
import { WBProfileAirplane, WBProfileAirplaneProps } from '@/domain/models'
import { Result } from '@/domain/protocols/Result'
import { FuelQuantityUnit, LinearMeasureUnit, WeightUnit } from '@/domain/protocols/Unit'
import { AuthState } from '@/domain/states/AuthState'

import {
  EnvelopeDTO,
  IWBProfileAirplaneRepository,
  RequestGetWBProfilesResponse,
  StationDTO,
  StationVariableArmDTO,
  WBProfileAirPlaneDTO
} from './IWBProfileAirplaneRepository'

import { NEXATLAS_API_BASE } from '@/infra/http/apiBaseUrls'
import type { IHttpClient } from '@/infra/http/IHttpClient'
import { normalizeUnit } from '@/utils/unit'

/**
 * O rótulo da unidade chega como texto livre e já vazou com grafia divergente (o catálogo publica
 * "us gal/h" com espaço). Canonizamos caixa, espaço e underscore para que a unidade escrita de outro
 * jeito ainda case com o enum — e **mantemos o rótulo cru quando não reconhecemos**.
 *
 * O recuo para a unidade de domínio, que os repositórios de aeronave usam, seria errado aqui: lá o
 * número gravado está em unidade de domínio e a coluna é só rótulo, mas no perfil o número está na
 * unidade que a linha declara. Cair em `kg`/`in` reinterpretaria o valor em silêncio.
 */
const _normalizeProfileUnits = (dto: {
  weightUnit: WeightUnit
  armUnit: LinearMeasureUnit
  fuelQuantityUnit: FuelQuantityUnit
}): Pick<WBProfileAirplaneProps, 'weightUnit' | 'armUnit' | 'fuelQuantityUnit'> => ({
  weightUnit: normalizeUnit.weight(dto.weightUnit) ?? dto.weightUnit,
  armUnit: normalizeUnit.linearMeasure(dto.armUnit) ?? dto.armUnit,
  fuelQuantityUnit: normalizeUnit.fuelQuantity(dto.fuelQuantityUnit) ?? dto.fuelQuantityUnit
})

/**
 * `capacityUnit` guarda peso nas estações de assento e carga e quantidade de combustível nas de
 * tanque. `FuelQuantityUnit` contém as duas unidades de peso, então um normalizador só cobre os dois
 * casos.
 */
const _normalizeCapacityUnit = <T extends WeightUnit | FuelQuantityUnit>(rawUnit: T): T =>
  (normalizeUnit.fuelQuantity(rawUnit) as T) ?? rawUnit

@injectable()
export class WBProfileAirplaneRepository implements IWBProfileAirplaneRepository {
  // Cache autoritativo em memória: uma vez carregado (via init/ensureLoaded), contém TODOS os perfis
  // do usuário. `undefined` = nunca carregado; array (mesmo vazio) = carregado. create/update/delete
  // mantêm este array in-place, então não é necessário re-buscar por aeronave (PLA-213).
  private wbProfiles: WBProfileAirplane[]
  // Deduplica carregamentos concorrentes (ex.: init fire-and-forget + primeira abertura do P&B).
  private loadPromise: Promise<Result<WBProfileAirplane[]>> | null = null

  constructor(
    @inject(InjectionTokens.HttpClient) private httpClient: IHttpClient,
    @inject(InjectionTokens.AuthState) private authState: AuthState
  ) {}

  protected getRemoteUrl(): string {
    return `${NEXATLAS_API_BASE}/wbProfileAirplane`
  }

  async init(): Promise<void> {
    await this.ensureLoaded()
  }

  // Garante que o cache completo de perfis esteja carregado, buscando remotamente apenas quando
  // necessário. Reaproveita o carregamento em andamento para evitar chamadas duplicadas.
  private async ensureLoaded(forceRefresh = false): Promise<void> {
    if (this.wbProfiles !== undefined && !forceRefresh) return
    if (forceRefresh || !this.loadPromise) {
      this.loadPromise = this._getRemoteWBProfiles()
    }
    try {
      await this.loadPromise
    } finally {
      this.loadPromise = null
    }
  }

  async getAll(forceRefresh = false): Promise<Result<WBProfileAirplane[]>> {
    try {
      await this.ensureLoaded(forceRefresh)
      if (this.wbProfiles !== undefined) {
        return Result.ok(this.wbProfiles.filter((profile) => !profile.deletedAt))
      }
      return Result.fail('Error fetching WB profiles')
    } catch (error) {
      console.error('Error in getAllWBProfiles:', error)
      return Result.fail(`Error fetching WB profiles: ${error}`)
    }
  }

  async getById(id: UniqueEntityID, forceRefresh = false): Promise<Result<WBProfileAirplane>> {
    try {
      await this.ensureLoaded(forceRefresh)
      if (this.wbProfiles && this.wbProfiles.length > 0) {
        const foundProfile = this.wbProfiles.find((profile) => profile.id.equals(id) && !profile.deletedAt)
        if (foundProfile) {
          return Result.ok(foundProfile)
        }
      }
      // Fallback remoto: o perfil pode não estar no cache (ex.: referenciado por um preenchimento antigo).
      const response = await this.httpClient.get<RequestGetWBProfilesResponse>(
        `${this.getRemoteUrl()}/${id?.toString()}`,
        {
          headers: {
            Authorization: `Token ${this.authState.getStateSnapshot().token}`
          }
        }
      )

      if (response.success === false || response.status === 500) {
        return Result.fail((response as any).error || 'Server error')
      } else if (response.status < 200 || response.status >= 300) {
        return Result.fail((response as any).message || 'Request failed')
      }

      return Result.ok(this._remoteToLocal(response.response))
    } catch (error) {
      console.error('Error in getWBProfileById:', error)
      return Result.fail(`Error fetching WB profile: ${error}`)
    }
  }

  async getByAircraft(aircraftId: UniqueEntityID, forceRefresh = false): Promise<Result<WBProfileAirplane[]>> {
    try {
      // Cache autoritativo: filtra do conjunto completo já carregado, sem GET por aeronave. Uma
      // aeronave sem perfis retorna [] (não re-busca remotamente) — os callers filtram/avisam (PLA-200).
      await this.ensureLoaded(forceRefresh)
      if (this.wbProfiles !== undefined) {
        const profiles = this.wbProfiles.filter(
          (profile) => profile.aircraftId.equals(aircraftId) && !profile.deletedAt
        )
        return Result.ok(profiles)
      }
      return Result.fail('Error fetching WB profiles for aircraft')
    } catch (error) {
      console.error('Error in getWBProfilesByAircraft:', error)
      return Result.fail(`Error fetching WB profiles for aircraft: ${error}`)
    }
  }

  async create(profile: WBProfileAirplane): Promise<Result<void>> {
    try {
      const dto = this._localToRemote(profile)
      const response = await this.httpClient.post(`${this.getRemoteUrl()}`, dto, {
        headers: {
          Authorization: `Token ${this.authState.getStateSnapshot().token}`
        }
      })
      if (response.success === false || response.status === 500) {
        return Result.fail((response as any).error || 'Server error')
      } else if (response.status < 200 || response.status >= 300) {
        return Result.fail((response as any).message || 'Request failed')
      }

      this.wbProfiles = this.wbProfiles ? [...this.wbProfiles, profile] : [profile]
      return Result.ok()
    } catch (error) {
      console.error('Error in saveWBProfile:', error)
      return Result.fail(`Error saving WB profile: ${error}`)
    }
  }

  /**
   * Editar um perfil **cunha um id novo**: a gravação é remoção mais criação de outra entidade, de
   * propósito, para que uma ficha já assinada continue apontando para a versão com que foi
   * calculada.
   *
   * O id novo nascia aqui dentro e morria aqui: com `Result<void>`, quem chamava não tinha como
   * saber para onde o perfil tinha ido — e o `defaultWbProfileId` da aeronave, que é um ponteiro
   * para o id antigo, ficava apontando para o perfil aposentado a cada edição (PLA-316). Devolver o
   * id novo é o mínimo para o remapeamento morar onde o ponteiro mora.
   */
  async update(profile: WBProfileAirplane): Promise<Result<UniqueEntityID>> {
    try {
      const deleteResult = await this.delete(profile.id)
      if (deleteResult.isFailure) return Result.fail(deleteResult.error)

      const newProfile = WBProfileAirplane.create(profile.props)

      const createResult = await this.create(newProfile)
      if (createResult.isFailure) return Result.fail(createResult.error)

      return Result.ok(newProfile.id)
    } catch (error) {
      return Result.fail(`Error updating WB profile: ${error}`)
    }
  }

  async delete(id: UniqueEntityID): Promise<Result<void>> {
    try {
      const response = await this.httpClient.delete(
        `${this.getRemoteUrl()}/${id?.toString()}`,
        {},
        {
          headers: {
            Authorization: `Token ${this.authState.getStateSnapshot().token}`
          }
        }
      )

      if (response.success === false || response.status === 500) {
        return Result.fail((response as any).error || 'Server error')
      } else if (response.status < 200 || response.status >= 300) {
        return Result.fail((response as any).message || 'Request failed')
      }

      if (this.wbProfiles) {
        this.wbProfiles = this.wbProfiles.filter((profile) => !profile.id.equals(id))
      }
      return Result.ok()
    } catch (error) {
      console.error('Error in deleteWBProfile:', error)
      return Result.fail(`Error deleting WB profile: ${error}`)
    }
  }

  // PRIVATE METHODS

  private async _getRemoteWBProfiles(): Promise<Result<WBProfileAirplane[]>> {
    const response = await this.httpClient.get<RequestGetWBProfilesResponse[]>(this.getRemoteUrl(), {
      headers: {
        Authorization: `Token ${this.authState.getStateSnapshot().token}`
      }
    })

    if (response.success === false || response.status === 500) {
      return Result.fail((response as any).error || 'Server error')
    } else if (response.status < 200 || response.status >= 300) {
      return Result.fail((response as any).message || 'Request failed')
    }

    const profiles = response.response
      .map((profile) => this._remoteToLocal(profile))
      .filter((profile) => !profile.deletedAt)
    this.wbProfiles = profiles
    return Result.ok(profiles)
  }

  private _localToRemote(profile: WBProfileAirplane): WBProfileAirPlaneDTO {
    return {
      id: profile.id ? profile.id?.toString() : new UniqueEntityID()?.toString(),
      userAircraftId: profile.aircraftId?.toString() || null,
      wbProfileAirplaneModelId: profile.wbProfileAirplaneModelId?.toString() || null,
      name: profile.name,
      // Sempre vazio: `reference` descreve o template (POH/AFM + part number + serial numbers), não o
      // perfil do usuário — a procedência é recuperável via `wbProfileAirplaneModelId`. A coluna do
      // backend aceita no máximo 255 caracteres e responde 500 acima disso, então zerar aqui blinda
      // create e update contra qualquer texto longo que ainda venha de um perfil legado carregado do
      // servidor. O campo é obrigatório no DTO remoto (null/ausente devolve 400), daí a string vazia.
      reference: '',
      weightUnit: profile.weightUnit,
      armUnit: profile.armUnit,
      fuelQuantityUnit: profile.fuelQuantityUnit,
      momentIndex: profile.momentIndex,
      lemac: profile.lemac,
      macLength: profile.macLength,
      basicEmptyWeight: profile.basicEmptyWeight,
      basicEmptyWeightArm: profile.basicEmptyWeightArm,
      maxTakeoffWeight: profile.maxTakeoffWeight,
      maxZeroFuelWeight: profile.maxZeroFuelWeight,
      maxRampWeight: profile.maxRampWeight,
      maxLandingWeight: profile.maxLandingWeight,
      stations: profile.stations?.map(
        (station) =>
          ({
            id: station.id?.toString(),
            name: station.name,
            type: station.type,
            capacityUnit: station.capacityUnit ?? null,
            fixedArm: station.fixedArm ?? null,
            maxCapacity: station.maxCapacity ?? null,
            includeInBOW: station.includeInBOW ?? false,
            predefinedWeight: station.predefinedWeight ?? null,
            armType: station.armType ?? null,
            priority: station.priority ?? null,
            variableArms:
              station.variableArms?.map(
                (arm) =>
                  ({
                    id: arm.id?.toString(),
                    weight: arm.weight,
                    moment: arm.moment
                  }) as StationVariableArmDTO
              ) ?? []
          }) as StationDTO
      ),
      // `pointSequence` fecha o polígono do envelope: sem ele o servidor devolve os pontos por peso,
      // que não é o contorno. Vai como `null` quando o ponto ainda não tem sequência — a gravação
      // renumera antes de chegar aqui, então isso só acontece para envelope sem coordenada.
      envelope: profile.envelope?.map(
        (env) =>
          ({
            id: env.id?.toString(),
            weight: env.weight,
            arm: env.arm,
            macPercent: env.macPercent,
            pointSequence: env.pointSequence ?? null
          }) as EnvelopeDTO
      )
    }
  }

  private _remoteToLocal(dto: RequestGetWBProfilesResponse): WBProfileAirplane {
    return WBProfileAirplane.create(
      {
        aircraftId: new UniqueEntityID(dto.userAircraftId),
        wbProfileAirplaneModelId: new UniqueEntityID(dto.wbProfileAirplaneModelId),
        name: dto.name,
        reference: dto.reference,
        ..._normalizeProfileUnits(dto),
        momentIndex: dto.momentIndex,
        lemac: dto.lemac,
        macLength: dto.macLength,
        basicEmptyWeight: dto.basicEmptyWeight,
        basicEmptyWeightArm: dto.basicEmptyWeightArm,
        maxTakeoffWeight: dto.maxTakeoffWeight,
        maxZeroFuelWeight: dto.maxZeroFuelWeight,
        maxRampWeight: dto.maxRampWeight,
        maxLandingWeight: dto.maxLandingWeight,
        stations: dto.stations?.map((station) => ({
          id: new UniqueEntityID(station.id),
          name: station.name,
          type: station.type,
          fixedArm: station.fixedArm,
          maxCapacity: station.maxCapacity,
          capacityUnit: _normalizeCapacityUnit(station.capacityUnit),
          includeInBOW: station.includeInBOW,
          predefinedWeight: station.predefinedWeight,
          armType: station.armType,
          priority: station.priority,
          variableArms: station.variableArms?.map((arm) => ({
            id: new UniqueEntityID(arm.id),
            weight: arm.weight,
            moment: arm.moment
          }))
        })),
        envelope: dto.envelope?.map((env) => ({
          id: new UniqueEntityID(env.id),
          weight: env.weight,
          arm: env.arm,
          macPercent: env.macPercent,
          pointSequence: env.pointSequence ?? undefined
        })),
        createdAt: new Date(dto.createdAt),
        updatedAt: new Date(dto.updatedAt),
        deletedAt: dto.deletedAt ? new Date(dto.deletedAt) : null
      },
      dto.id ? new UniqueEntityID(dto.id) : undefined
    )
  }
}
